import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useNavigate } from 'react-router-dom';
import { FormGroup, Input, Label } from 'reactstrap';
import crypto from 'crypto-js';

import { MS_PER_SECOND, SESSION_RECHECK_MS } from '~constants/session';

import { describeSessionLimit, formatCountdown } from '~libs/sessionCountdown';
import { expireSession, resolveExpiredSession } from '~libs/sessionExpiry';
import { resolveReauthWaiters } from '~libs/sessionReauth';
import { publishSessionMessage } from '~libs/sessionSync';
import { externalNavigation, leaveForTp1SignIn, tp1SignInUrl, usesSsoSignIn } from '~libs/signInRoute';

import { Button, ErrorResponse, Modal } from '~components';

import { fetchSessionStatus, reauthSession, SessionRejectedPayload, toSessionTiming } from '~main/actions/session';

import { setForceRedirect, setReauthPrompt } from '~main/reducers/login';

import { selectAuthenticatedUser, selectReauthPrompt, selectSession } from '~main/selectors/login';

const TICK_MS = MS_PER_SECOND;
const INVALID_MESSAGE = "That password isn't right. Please try again.";
const PASSWORD_REQUIRED_MESSAGE = 'Enter your password to continue.';
const GENERIC_MESSAGE = 'We could not confirm your password. Please try again.';
const ORGANISATION_PENDING_MESSAGE = "Finish signing in on the new tab. This page will carry on once you're signed in.";
const ORGANISATION_BLOCKED_MESSAGE =
  'Your browser blocked the new tab. Allow pop-ups for this site and try again, or sign in on this tab instead (anything not saved on this page will be lost).';
const LOGIN_SECRET = process.env.REACT_APP_LOGIN_AUTHENTICATION_SECRET ?? '';
const noop = () => undefined;

/**
 * Asks for the password in place, over the page, so nothing typed is lost. It opens for two reasons:
 * - `required`: the 12 hour limit has passed, or the API answered `reauth-required`. Requests that were held
 *   wait behind it and are replayed on success;
 * - `early`: the user chose "Confirm password now" from the expiry overlay, and can dismiss with "Not now".
 * A success starts a fresh window on the same browser key. Running out of time ends the session.
 *
 * Users who signed in through their organisation's single sign-on (TP1 with SSO) have no TAV password, so they
 * get no password form: instead they sign in through TP1 in a new tab. This page stays put: the new tab announces
 * the fresh session ('reauthed'), and as a fallback this page checks again whenever it is returned to.
 */
const SessionReauthModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const prompt = useAppSelector(selectReauthPrompt);
  const session = useAppSelector(selectSession);
  const user = useAppSelector(selectAuthenticatedUser);

  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [organisationPending, setOrganisationPending] = useState(false);
  const [organisationBlocked, setOrganisationBlocked] = useState(false);
  const passwordRef = useRef<HTMLInputElement>(null);
  const deadlineHandledFor = useRef<number | null>(null);
  const recheckTimer = useRef<ReturnType<typeof setTimeout>>();

  const isOpen = prompt !== 'none';
  const reauthExpiresAt = session?.reauthExpiresAt ?? null;

  // On open, learn the exact reauth deadline (a read that does not slide the session).
  useEffect(() => {
    if (!isOpen) {
      setPassword('');
      setError(null);
      setOrganisationPending(false);
      setOrganisationBlocked(false);
      deadlineHandledFor.current = null;
      return;
    }
    dispatch(fetchSessionStatus()).then((result) => {
      // Another tab may have confirmed the password already, which frees anything held here.
      if (fetchSessionStatus.fulfilled.match(result) && !result.payload.reauth_required) resolveReauthWaiters();
    });
  }, [isOpen, dispatch]);

  // After the organisation option, re-check when the user comes back to this tab: the 'reauthed' message from the
  // new tab may not arrive (no BroadcastChannel, or the sign-in finished in another window).
  useEffect(() => {
    if (!isOpen || !organisationPending) return undefined;
    let checking = false;
    const recheck = () => {
      if (checking || document.visibilityState === 'hidden') return;
      checking = true;
      dispatch(fetchSessionStatus()).then((result) => {
        checking = false;
        if (!fetchSessionStatus.fulfilled.match(result) || result.payload.reauth_required) return;
        resolveReauthWaiters();
        dispatch(setReauthPrompt('none'));
      });
    };
    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', recheck);
    return () => {
      window.removeEventListener('focus', recheck);
      document.removeEventListener('visibilitychange', recheck);
    };
  }, [isOpen, organisationPending, dispatch]);

  // Live countdown, and the deadline: running out of time ends the session.
  useEffect(() => {
    if (!isOpen) return undefined;
    const timer = setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (reauthExpiresAt !== null && current >= reauthExpiresAt && deadlineHandledFor.current !== reauthExpiresAt) {
        deadlineHandledFor.current = reauthExpiresAt;
        const deadline = reauthExpiresAt;
        resolveExpiredSession(dispatch).then((result) => {
          // The API could not be asked: re-arm this deadline so a later tick asks again.
          if (result !== 'unavailable') return;
          recheckTimer.current = setTimeout(() => {
            if (deadlineHandledFor.current === deadline) deadlineHandledFor.current = null;
          }, SESSION_RECHECK_MS);
        });
      }
    }, TICK_MS);
    return () => {
      clearInterval(timer);
      clearTimeout(recheckTimer.current);
    };
  }, [isOpen, reauthExpiresAt]);

  const reject = (payload: SessionRejectedPayload | undefined) => {
    switch (payload?.reason) {
      case 'invalid':
        setError(INVALID_MESSAGE);
        break;
      case 'password-required':
        setError(PASSWORD_REQUIRED_MESSAGE);
        break;
      case 'user-locked':
        setError(payload.message);
        break;
      case 'company-disabled':
        setError(payload.message);
        dispatch(setForceRedirect());
        break;
      case 'session-expired':
        expireSession(dispatch, 'expired');
        break;
      case 'proof-invalid':
        expireSession(dispatch, 'unverified');
        break;
      default:
        setError(payload?.message ?? GENERIC_MESSAGE);
    }
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (pending) return;
    if (!password) {
      setError(PASSWORD_REQUIRED_MESSAGE);
      return;
    }

    setPending(true);
    setError(null);
    const result = await dispatch(reauthSession({ encryptedPassword: crypto.AES.encrypt(password, LOGIN_SECRET).toString() }));
    setPending(false);

    if (reauthSession.fulfilled.match(result)) {
      setPassword('');
      // The reducer has closed the modal. Release the held requests and tell the other tabs to close theirs.
      const { expiresAt, absoluteExpiresAt, reauthExpiresAt: nextReauthExpiresAt } = toSessionTiming(result.payload);
      resolveReauthWaiters();
      publishSessionMessage({
        type: 'reauthed',
        expiresAt,
        absoluteExpiresAt,
        reauthExpiresAt: nextReauthExpiresAt,
      });
      return;
    }

    reject(result.payload);
  };

  const onNotNow = () => {
    dispatch(setReauthPrompt('none'));
  };

  const onSignInWithOrganisation = () => {
    const url = tp1SignInUrl();
    if (!url) return;
    // A blocked new tab must never leave the user stuck: say so, and offer this tab instead
    const opened = externalNavigation.open(url);
    setOrganisationBlocked(!opened);
    setOrganisationPending(opened);
  };

  const onSignInOnThisTab = () => {
    leaveForTp1SignIn({ ignoreGuard: true });
  };

  const onSignOut = () => {
    navigate('/logout');
  };

  const remaining = reauthExpiresAt !== null ? formatCountdown(reauthExpiresAt - now) : null;
  const limit = session ? describeSessionLimit(session.absoluteTimeoutSeconds) : '';
  const isSso = usesSsoSignIn();

  if (isSso) {
    return (
      <Modal
        isOpen={isOpen}
        setIsOpen={noop}
        header="Sign in again"
        disableClose
        backdrop="static"
        keyboard={false}
        centered
        footer={
          <>
            <Button color="success" onClick={onSignInWithOrganisation} dataCy="session-reauth-organisation-button">
              Sign in with your organisation
            </Button>
            {prompt === 'early' && (
              <Button color="secondary" onClick={onNotNow} dataCy="session-reauth-cancel-button">
                Not now
              </Button>
            )}
            <Button color="secondary" onClick={onSignOut} dataCy="session-reauth-signout-button">
              Sign out
            </Button>
          </>
        }
      >
        <span className="visually-hidden" role="status">
          Sign in with your organisation to keep your session active.
        </span>
        <p>
          {prompt === 'early'
            ? 'Sign in with your organisation to keep your session active.'
            : `You have reached the ${limit} session limit. Sign in with your organisation to keep working.`}{' '}
          Nothing on this page will be lost.
        </p>
        {organisationPending && (
          <p role="status" data-cy="session-reauth-organisation-pending">
            {ORGANISATION_PENDING_MESSAGE}
          </p>
        )}
        {organisationBlocked && (
          <div role="alert" data-cy="session-reauth-organisation-blocked">
            <p className="mb-1">{ORGANISATION_BLOCKED_MESSAGE}</p>
            <Button
              color="link"
              className="p-0 mb-3"
              onClick={onSignInOnThisTab}
              dataCy="session-reauth-this-tab-button"
            >
              Sign in on this tab instead
            </Button>
          </div>
        )}
        {remaining !== null && (
          <p className="mb-0">
            Time remaining to sign in:{' '}
            <strong role="timer" aria-live="off" data-cy="session-reauth-countdown">
              {remaining}
            </strong>
          </p>
        )}
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={noop}
      header="Confirm your password"
      disableClose
      backdrop="static"
      keyboard={false}
      centered
      onOpened={() => passwordRef.current?.focus()}
      footer={
        <>
          <Button
            color="success"
            type="submit"
            form="session-reauth-form"
            loading={pending}
            dataCy="session-reauth-confirm-button"
          >
            Confirm
          </Button>
          {prompt === 'early' && (
            <Button color="secondary" disabled={pending} onClick={onNotNow} dataCy="session-reauth-cancel-button">
              Not now
            </Button>
          )}
          <Button color="secondary" disabled={pending} onClick={onSignOut} dataCy="session-reauth-signout-button">
            Sign out
          </Button>
        </>
      }
    >
      <span className="visually-hidden" role="status">
        Confirm your password to keep your session active.
      </span>
      <p>
        {prompt === 'early'
          ? 'Confirm your password to keep your session active.'
          : `You have reached the ${limit} session limit. Confirm your password to keep your session active.`}{' '}
        Nothing on this page will be lost.
      </p>
      <form id="session-reauth-form" onSubmit={onSubmit} noValidate>
        {/* Lets password managers file the password against the right account. */}
        <input
          type="text"
          name="username"
          autoComplete="username"
          value={user?.email ?? ''}
          readOnly
          tabIndex={-1}
          aria-hidden="true"
          className="visually-hidden"
        />
        <FormGroup>
          <Label for="session-reauth-password">Password</Label>
          <Input
            innerRef={passwordRef}
            type="password"
            id="session-reauth-password"
            name="password"
            autoComplete="current-password"
            value={password}
            disabled={pending}
            onChange={({ target: { value } }) => setPassword(value)}
            data-cy="session-reauth-password-input"
          />
        </FormGroup>
        <ErrorResponse message={error} dataCy="session-reauth-error" />
      </form>
      {remaining !== null && (
        <p className="mb-0">
          Time remaining to confirm:{' '}
          <strong role="timer" aria-live="off" data-cy="session-reauth-countdown">
            {remaining}
          </strong>
        </p>
      )}
    </Modal>
  );
};

export default SessionReauthModal;
